import { useEffect, useRef } from 'react'
import { usePersona } from '@/features/business/hooks/usePersona'
import { BUSINESS_PROFILE_MODAL_ANALYTIC_SLUG } from '@/features/business/constants'
import { type ModalSection } from '@/features/cms/components/CMS/types'
import { useHandleDialog } from '@/features/shared/state/Dialog/useHandleDialog'
import { useGetImperativeIsDialogOpened } from '@/features/shared/state/Dialog/hooks'
import { getComponentForContentTypeId } from '@/features/cms/components/CMS/componentUtils'
import { useCanDisplayCMSModal } from '@/features/shared/hooks/useCanDisplayCMSModal'

declare global {
  interface Window {
    __E2E__?: boolean
  }
}

export const CMSModalSection = ({
  section,
  suppress = false,
}: {
  section: ModalSection
  suppress?: boolean
}) => {
  const { openDialog } = useHandleDialog()
  const getIsDialogOpened = useGetImperativeIsDialogOpened()
  /** prevents consecutive dialogs from displaying */
  const hasShownDialog = useRef(false)
  const canDisplayCMSModal = useCanDisplayCMSModal(suppress)

  // only the first modal returned from the CMS will be displayed. When
  // we mark a modal as viewed in view tracker, CMS will stop returning
  // that modal in the response according to the view rules configured
  // from the CMS.
  const modalData = section[0]

  const { shouldShowBusinessNudge } = usePersona()

  useEffect(() => {
    if (!modalData) return
    if (hasShownDialog.current) return
    if (getIsDialogOpened()) {
      hasShownDialog.current = true
      return
    }
    // We don't want CMS content to dictate whether our E2E tests pass
    if (window.__E2E__) return
    if (!canDisplayCMSModal) return

    const Component = getComponentForContentTypeId({
      contentTypeId: modalData.content_type_id,
    })

    // If this is the business modal, only show if the user is eligible
    if (
      modalData.analytic_slug === BUSINESS_PROFILE_MODAL_ANALYTIC_SLUG &&
      !shouldShowBusinessNudge
    )
      return

    if (Component) {
      hasShownDialog.current = true
      openDialog(Component, modalData)
    }
  }, [canDisplayCMSModal, getIsDialogOpened, modalData, openDialog, shouldShowBusinessNudge])

  return null
}
